import { useState } from "react";
import { Outlet, useNavigate, useLocation } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Sidebar } from "./Sidebar";
import { ShowSelector } from "@/components/ui/ShowSelector";
import { NotificationBell } from "@/components/ui/NotificationBell";
import { ShowProvider, useShows } from "@/context/ShowContext";
import { PricingLimitedFooter } from "@/components/ui/PricingLimitedNotice";
import { useAuth } from "@/context/AuthContext";
import { useCeremonyPoller } from "@/hooks/useCeremonyPoller";
import { gamificationApi } from "@/api/gamification.api";

function ShowStateLabel() {
  const { selectedShow } = useShows();
  if (!selectedShow?.first_perf_date) return null;

  const today = new Date().toISOString().slice(0, 10);
  let label: string;

  if (selectedShow.last_perf_date && selectedShow.last_perf_date < today) {
    label = "SEASON COMPLETE";
  } else if (selectedShow.first_perf_date > today) {
    label = "PRE-SEASON";
  } else {
    label = "IN-SEASON";
  }
  const color = "#8c8fa1";

  return (
    <span className="hidden sm:block whitespace-nowrap relative z-10" style={{ fontSize: "12px", fontWeight: 800, letterSpacing: "0.12em", color, marginRight: "12px" }}>{label}</span>
  );
}

function TopBarStats() {
  const { data: progress } = useQuery({
    queryKey: ["gamification-progress"],
    queryFn: gamificationApi.getProgress,
    staleTime: 60_000,
  });
  if (!progress) return null;

  const xp = progress.xp_total ?? 0;
  const lvl = progress.level;

  return (
    <div className="flex items-center gap-2.5 mr-1">
      <span className="text-[9px] font-bold hidden sm:block" style={{ color: "#9695a4" }}>
        L{lvl?.number ?? 1}: {lvl?.name ?? "Newcomer"}
      </span>
      <span className="text-[9px] font-bold" style={{ color: "#b87800" }}>
        {xp.toLocaleString()} XP
      </span>
    </div>
  );
}

function FloatingHelpButton() {
  const navigate = useNavigate();
  const location = useLocation();
  const [hovered, setHovered] = useState(false);
  if (location.pathname === "/reference") return null;

  const pills = [
    { label: "HELP", delay: 0 },
    { label: "REFERENCE", delay: 60 },
    { label: "FAQ", delay: 140 },
  ];

  return (
    <div
      className="fixed z-40"
      style={{ bottom: "112px", right: "40px" }}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      <div style={{ position: "absolute", right: "100%", top: "50%", transform: "translateY(-50%)", display: "flex", alignItems: "center", gap: "3px", paddingRight: "6px" }}>
        {pills.map((pill) => (
          <div
            key={pill.label}
            onClick={() => navigate("/reference")}
            style={{
              background: "rgb(42 74 158 / 85%)",
              color: "#fff",
              fontSize: "0.35rem",
              fontWeight: 700,
              letterSpacing: "0.05em",
              padding: "0.15rem 0.38rem",
              borderRadius: "0.5rem",
              cursor: "pointer",
              whiteSpace: "nowrap",
              opacity: hovered ? 1 : 0,
              transform: hovered ? "translateX(0)" : "translateX(10px)",
              transition: `opacity 0.2s ease ${pill.delay}ms, transform 0.3s cubic-bezier(0.22,1,0.36,1) ${pill.delay}ms`,
              boxShadow: "0 1px 6px rgba(42, 74, 158, 0.3)",
            }}
          >
            {pill.label}
          </div>
        ))}
      </div>
      <button
        onClick={() => navigate("/reference")}
        title="Reference Center"
        className="flex items-center justify-center rounded-full shadow-lg transition-all duration-200 hover:scale-110"
        style={{
          width: "36px",
          height: "36px",
          background: "rgb(42 74 158 / 85%)",
          boxShadow: "0 2px 12px rgba(42, 74, 158, 0.4)",
          color: "#fff",
          fontSize: "16px",
          fontWeight: 700,
          border: "none",
          cursor: "pointer",
        }}
      >
        ?
      </button>
    </div>
  );
}

export function AppShell() {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [sidebarExpanded, setSidebarExpanded] = useState(false);
  const { user } = useAuth();
  const navigate = useNavigate();
  const initials = (user?.displayName || user?.username || "?").charAt(0).toUpperCase();

  // Poll for server-triggered ceremonies (every 30s)
  useCeremonyPoller();

  return (
    <ShowProvider>
      <div className="flex flex-col h-dvh overflow-hidden" style={{ background: "#1e1f2c" }}>
        {/* TOP BAR — fixed, 34px, full width */}
        <header className="flex items-center gap-3 px-3 md:px-4 flex-shrink-0 relative"
          style={{
            height: "34px", minHeight: "34px", maxHeight: "34px", zIndex: 50,
            backgroundColor: "#313949",
            backgroundImage: "linear-gradient(to right, rgba(67,74,92,0) 0%, #475165 25%, #434a5c 50%, rgba(67,74,92,0) 100%), url('/noise.png')",
            backgroundRepeat: "no-repeat, repeat",
          }}>
          <div className="absolute inset-0 overflow-hidden pointer-events-none">
            <span className="absolute select-none font-extrabold" style={{ fontSize: "56px", lineHeight: "1", color: "#68717c", left: "-15px", top: "50%", transform: "translateY(-50%)" }}>SMT</span>
          </div>
          {/* Hamburger — mobile only */}
          <button onClick={() => setSidebarOpen(true)} className="md:hidden text-[#808297] hover:text-[#fafafc] p-1 relative z-10">
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}><path strokeLinecap="round" strokeLinejoin="round" d="M3.75 6.75h16.5M3.75 12h16.5m-16.5 5.25h16.5" /></svg>
          </button>
          <div className="flex-1 flex justify-end relative z-10">
            <ShowStateLabel />
          </div>
          <div className="relative z-10">
            <ShowSelector />
          </div>
          <div className="flex-1" />
          <div className="flex items-center gap-2 relative z-10">
            <TopBarStats />
            <NotificationBell />
            <button onClick={() => navigate("/profile")} className="flex items-center gap-2 hover:opacity-80 transition-opacity" title="Profile">
              <div className="rounded-full bg-gradient-to-br from-[#32caab] to-[#28da90] flex items-center justify-center text-[9px] font-bold text-white" style={{ width: "14px", height: "14px" }}>{initials}</div>
              <span className="text-[10px] text-[#9695a4] font-medium hidden sm:block">{user?.displayName || user?.username}</span>
            </button>
          </div>
        </header>

        {/* BODY */}
        <div className="flex flex-1 overflow-hidden">
          {/* LEFT COLUMN: logo above sidebar — desktop only */}
          <div className="hidden md:flex flex-col flex-shrink-0 absolute z-30 h-[calc(100dvh-34px)] transition-all duration-200"
            style={{ width: sidebarExpanded ? "160px" : "38px" }}
            onMouseEnter={() => setSidebarExpanded(true)}
            onMouseLeave={() => setSidebarExpanded(false)}>
            <div className="flex items-center flex-shrink-0 pl-[9px]" style={{ height: "40px" }}>
              <svg className="text-[#b87800]" style={{ width: "18px", height: "18px" }} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M16.5 6v.75m0 3v.75m0 3v.75m0 3V18m-9-5.25h5.25M7.5 15h3M3.375 5.25c-.621 0-1.125.504-1.125 1.125v3.026a2.999 2.999 0 010 5.198v3.026c0 .621.504 1.125 1.125 1.125h17.25c.621 0 1.125-.504 1.125-1.125v-3.026a2.999 2.999 0 010-5.198V6.375c0-.621-.504-1.125-1.125-1.125H3.375z" />
              </svg>
            </div>
            <div className="flex-1 overflow-hidden" style={{ backgroundColor: "#313949", backgroundImage: "url('/noise.png')", backgroundRepeat: "repeat", boxShadow: "0 0 10px 10px #191c2a" }}>
              <Sidebar isOpen={sidebarOpen} onClose={() => setSidebarOpen(false)} expanded={sidebarExpanded} />
            </div>
          </div>
          {/* Spacer to keep content from overlapping collapsed sidebar */}
          <div className="hidden md:block flex-shrink-0" style={{ width: "38px" }} />
          {/* Mobile sidebar overlay */}
          <div className="md:hidden">
            <Sidebar isOpen={sidebarOpen} onClose={() => setSidebarOpen(false)} expanded={false} />
          </div>

          {/* MAIN CONTENT — responsive padding */}
          <main className="flex-1 overflow-y-auto px-3 pb-3 md:pb-3 md:pr-3 md:pl-0" style={{ marginLeft: "0" }}>
            <div className="md:ml-4 transition-all duration-200" style={{ paddingLeft: sidebarExpanded ? "122px" : "0" }}>
              {/* DASHBOARD label — scrolls with content, shifts with sidebar */}
              <div className="flex items-center" style={{ height: "40px" }}>
                <span className="hidden md:block font-medium text-[#fafafc] tracking-widest" style={{ fontSize: "10px", textShadow: "0 0 20px #222435" }}>DASHBOARD</span>
                <span className="md:hidden font-medium text-[#fafafc] tracking-widest" style={{ fontSize: "10px", textShadow: "0 0 20px #222435" }}>DASHBOARD</span>
              </div>
              <Outlet />
            </div>
          </main>
        </div>
        <PricingLimitedFooter />
        <FloatingHelpButton />
      </div>
    </ShowProvider>
  );
}
